import { ComponentChildren } from 'preact';
import type {
  States,
  LinkRendererContext,
  DiagramLinkData,
  DiagramLayoutOutput,
  Bounds,
  DiagramLabelPosition
} from './diagram.types';
import { DiagramLinkElement } from './DiagramLinkElement';
import { layerStyles, linkLayerStyle } from './themes/DiagramStyles.css';
import { getZoomToFitZoom } from './utils/zoomUtils';
import { DiagramLabelLayer } from './DiagramLabelLayer';
import { TextDimensions } from '#hooks/PRIVATE_useTextDimensions';

type Props<K2, K1, D2, L> = {
  links: D2[];
  states: States<K1, K2, D2>;
  linkRenderer: (context: LinkRendererContext<K1, D2, L>) => ComponentChildren;
  width: number;
  height: number;
  supportsSelection: boolean;
  layoutOutput: L;
  combinedNodeBounds: Bounds;
  labelDimensions: Map<K1 | K2, TextDimensions>;
  linkLabelPositions: Map<K2, DiagramLabelPosition>;
  onLinkSizeChanged: (width: number, height: number, id: K2) => void;
  minX: number;
  minY: number;
};

export function DiagramLinkLayer<
  K2 extends string | number,
  K1 extends string | number,
  D2 extends DiagramLinkData<K2, K1>,
  L extends DiagramLayoutOutput<K1, K2>
>({
  links,
  states,
  linkRenderer,
  width,
  height,
  supportsSelection,
  onLinkSizeChanged,
  layoutOutput,
  combinedNodeBounds,
  linkLabelPositions,
  labelDimensions,
  minX,
  minY
}: Props<K2, K1, D2, L>) {
  const zoom = getZoomToFitZoom(width, height, combinedNodeBounds);
  return (
    <div class={linkLayerStyle} style={{ top: -minY, left: -minX }}>
      <svg width={width / zoom} height={height / zoom} class={layerStyles} overflow="visible">
        {links.map((link) => {
          const state = {
            isSelected: states.selectedLinkIds.includes(link.id),
            isFocused: states.focusedId === link.id,
            isHovered: states.hoveredId === link.id,
            zoom: states.zoom
          };
          return (
            <DiagramLinkElement
              id={link.id}
              state={state}
              isDimmed={
                (states.highlightedNodeIds.length !== 0 ||
                  states.highlightedLinkIds.length !== 0 ||
                  states.highlightedPromotedLinkIds.length !== 0) &&
                !states.highlightedLinkIds.includes(link.id)
              }
              activeId={states.activeId}
              accessibleLabel={link.accessibleLabel}
              data={link}
              linkRenderer={linkRenderer}
              supportsSelection={supportsSelection}
              layoutOutput={layoutOutput}
              onLinkSizeChanged={onLinkSizeChanged}
            />
          );
        })}
      </svg>
      {labelDimensions ? (
        <div>
          {links && linkLabelPositions ? (
            <DiagramLabelLayer
              labelPosition={linkLabelPositions}
              itemProps={links}
              labelDimensions={labelDimensions}
            />
          ) : undefined}
        </div>
      ) : undefined}
    </div>
  );
}
